import { getCompetitorChannelTypeId } from "../../components/organization-settings/competitorChannelTypes"
import { normalizeSocialNetworkUsername } from "../../store/slices/entitySocialNetworkValidateSlice"

function trimStr(value) {
  return String(value ?? "").trim()
}

function positiveInt(value) {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

export function mapCompetitorDraftToCreateEntityPayload(draft, clientId, socialNetworkTypes = []) {
  const socialNetworkAccounts = (draft?.socialChannels || [])
    .filter((sc) => sc?.channel && trimStr(sc.url))
    .map((sc) => {
      const typeId = getCompetitorChannelTypeId(sc.channel, socialNetworkTypes)
      if (!typeId) return null
      const username =
        trimStr(sc.username) || normalizeSocialNetworkUsername(sc.url)
      return {
        socialNetworkType: {
          id: typeId,
          description: sc.channel,
        },
        socialNetworkId: sc.socialNetworkId ?? null,
        socialNetworkUsername: username,
        socialNetworkUrl: trimStr(sc.url),
      }
    })
    .filter(Boolean)

  const payload = {
    name: trimStr(draft?.property),
    url: trimStr(draft?.tripadvisorUrl),
    clientId: positiveInt(clientId),
    socialNetworkAccounts,
  }

  const cityId = positiveInt(draft?.cityId)
  if (cityId) payload.cityId = cityId

  const subBrandId = positiveInt(draft?.subBrandId)
  if (subBrandId) payload.subBrandId = subBrandId

  const websiteUrl = trimStr(draft?.websiteUrl)
  if (websiteUrl) payload.websiteUrl = websiteUrl

  return payload
}

export function mapCompetitorDraftToUpdateEntityPayload(draft, clientId) {
  const payload = {
    name: trimStr(draft?.property),
    url: trimStr(draft?.tripadvisorUrl),
    websiteUrl: trimStr(draft?.websiteUrl),
    clientId: positiveInt(clientId),
  }

  const cityId = positiveInt(draft?.cityId)
  if (cityId) payload.cityId = cityId

  const subBrandId = positiveInt(draft?.subBrandId)
  if (subBrandId) payload.subBrandId = subBrandId

  return payload
}

export function extractCreatedSocialAccount(response) {
  const row = response?.account ?? response?.data ?? response
  if (row == null || typeof row !== "object") {
    return { accountId: null, socialNetworkId: null, username: "", url: "" }
  }
  const id = Number(row.id ?? row.socialNetworkAccountId ?? row.entitySocialNetworkAccountId)
  return {
    accountId: Number.isFinite(id) && id > 0 ? id : null,
    socialNetworkId: row.socialNetworkId ?? response?.socialNetworkId ?? null,
    username: trimStr(row.socialNetworkUsername),
    url: trimStr(row.socialNetworkUrl),
  }
}

export function extractSocialNetworkAccountId(response) {
  return extractCreatedSocialAccount(response).accountId
}

export function validateCreateEntityPayload(payload) {
  const errors = {}

  if (!payload.name) errors.property = "Property name is required"
  if (!payload.url) errors.tripadvisorUrl = "TripAdvisor URL is required"
  if (!payload.clientId) errors.client = "Client is required"

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  }
}

export function extractCreatedEntityId(response) {
  const row = response?.data ?? response
  const id = row?.id ?? row?.entityId
  const num = Number(id)
  return Number.isFinite(num) && num > 0 ? num : null
}

export function mapCreateEntityResponseToCompetitor(response, draft) {
  const row = response?.data ?? response
  if (!row || typeof row !== "object") {
    return {
      id: `comp-${Date.now()}`,
      entityId: null,
      property: trimStr(draft?.property),
      type: trimStr(draft?.type),
      location: trimStr(draft?.location),
      country: trimStr(draft?.country),
      countryId: draft?.countryId ?? null,
      city: trimStr(draft?.city),
      cityId: draft?.cityId ?? null,
      brand: trimStr(draft?.brand),
      subBrand: trimStr(draft?.subBrand),
      websiteUrl: trimStr(draft?.websiteUrl),
      tripadvisorUrl: trimStr(draft?.tripadvisorUrl),
      socialChannels: draft?.socialChannels ?? [],
    }
  }

  return {
    id: String(row.id ?? `comp-${Date.now()}`),
    entityId: row.id ?? null,
    property: trimStr(row.name ?? draft?.property),
    type: trimStr(row.propertyType ?? draft?.type),
    location: trimStr(draft?.location),
    country: trimStr(draft?.country),
    countryId: draft?.countryId ?? null,
    city: trimStr(draft?.city),
    cityId: draft?.cityId ?? null,
    brand: trimStr(draft?.brand),
    subBrand: trimStr(draft?.subBrand),
    websiteUrl: trimStr(row.websiteUrl ?? draft?.websiteUrl),
    tripadvisorUrl: trimStr(row.url ?? draft?.tripadvisorUrl),
    socialChannels: draft?.socialChannels ?? [],
  }
}
